import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Obj } from '@popperjs/core';

/**
 * HTTP utility service for making API requests with common error handling and configuration.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  /** Base API URL from environment */
  private baseUrl = '';
  private http = inject(HttpClient);

  constructor() {
    this.baseUrl = environment.apiUrl;
  }

  /**
   * Performs a GET request.
   * @param endpoint API endpoint (relative to baseUrl)
   * @param params Optional query parameters
   * @param options Optional HTTP options (headers, etc.)
   */
  get<T>(endpoint: string, params?: Obj, options: { headers?: HttpHeaders } = {}): Observable<T> {
    return this.http
      .get<T>(`${this.baseUrl}${endpoint}`, {
        params,
        withCredentials: true,
        ...options,
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Performs a POST request.
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   * @param options Optional HTTP options (headers, etc.)
   */
  post<T>(endpoint: string, body: Partial<Obj>, options: { headers?: HttpHeaders } = {}): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}${endpoint}`, body, {
        withCredentials: true,
        ...options,
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Performs a PUT request.
   * @param endpoint API endpoint (relative to baseUrl)
   * @param body Request body
   * @param options Optional HTTP options (headers, etc.)
   */
  put<T>(endpoint: string, body: Partial<Obj>, options: { headers?: HttpHeaders } = {}): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}${endpoint}`, body, {
        ...options,
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Performs a DELETE request.
   * @param endpoint API endpoint (relative to baseUrl)
   * @param options Optional HTTP options (headers, etc.)
   */
  delete<T>(endpoint: string, options: { headers?: HttpHeaders } = {}): Observable<T> {
    return this.http
      .delete<T>(`${this.baseUrl}${endpoint}`, {
        ...options,
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Handles HTTP errors and returns a user-friendly observable error.
   * @param error The error thrown by HttpClient
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMsg = 'An unknown error occurred.';
    if (error.error instanceof ErrorEvent) {
      // Client-side/network error
      errorMsg = `Client error: ${error.error.message}`;
    } else if (error instanceof HttpErrorResponse) {
      // Backend error
      errorMsg = `Server error (${error.status}): ${error.message}`;
    }
    console.error('HTTP request failed', error);
    return throwError(() => new Error(errorMsg));
  }
}
